import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, FileSpreadsheet } from 'lucide-react'
import { useSolicitudes, solicitudesKeys, useActualizarSeguimiento } from '../hooks/useSolicitudes'
import { useCampania } from '../hooks/useCampanias'
import { actualizarEstado as actualizarEstadoApi } from '../api/muestrasApi'
import { SolicitudesFiltros } from '../components/SolicitudesFiltros'
import { SolicitudesTabla } from '../components/SolicitudesTabla'
import { SeguimientoModal } from '../components/SeguimientoModal'
import { ExportarButtons } from '../components/ExportarButtons'
import type { FiltrosSolicitudes, ActualizarEstadoPayload, ActualizarSeguimientoPayload } from '../types'

export function ReporteDatosPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)
  const { setOverride, clearOverride } = useBreadcrumb()
  const { data: campania } = useCampania(campaniaId)

  useDocumentTitle(campania ? `Solicitudes - ${campania.titulo}` : 'Solicitudes')

  // Breadcrumb: mostrar nombre de campaña en vez de ID
  useEffect(() => {
    if (campania?.titulo) {
      setOverride(`/muestras-medicas/${id}`, campania.titulo)
    }
    return () => {
      clearOverride(`/muestras-medicas/${id}`)
    }
  }, [campania?.titulo, id, setOverride, clearOverride])

  const [filtros, setFiltros] = useState<FiltrosSolicitudes>({ page: 1, page_size: 25 })
  const [selectedSolicitud, setSelectedSolicitud] = useState<number | null>(null)

  const queryClient = useQueryClient()
  const { data, isLoading } = useSolicitudes(campaniaId, filtros)
  const actualizarSeguimiento = useActualizarSeguimiento(campaniaId)

  const handleEstadoChange = async (payload: ActualizarEstadoPayload) => {
    try {
      await actualizarEstadoApi(payload)
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.all })
      toast.success('Estado actualizado')
    } catch {
      toast.error('Error al actualizar estado')
    }
  }

  const handleSaveSeguimiento = async (payload: ActualizarSeguimientoPayload) => {
    await actualizarSeguimiento.mutateAsync(payload)
    setSelectedSolicitud(null)
  }

  const handleClearFiltros = () => {
    setFiltros({ page: 1, page_size: 25 })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campaniaId}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-teal-500" />
            Solicitudes
          </h1>
        </div>
        <ExportarButtons campaniaId={campaniaId} />
      </div>

      {/* Filtros */}
      <SolicitudesFiltros
        filtros={filtros}
        filtrosDisponibles={data?.filtros_disponibles}
        onFiltrosChange={setFiltros}
        onClear={handleClearFiltros}
      />

      {/* Tabla */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data && data.results.length > 0 ? (
          <div className="p-4">
            <SolicitudesTabla
              solicitudes={data.results}
              camposDinamicos={data.campos_dinamicos}
              onEstadoChange={handleEstadoChange}
              onRowClick={(id) => setSelectedSolicitud(id)}
            />

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {((filtros.page || 1) - 1) * (filtros.page_size || 25) + 1} -{' '}
                  {Math.min((filtros.page || 1) * (filtros.page_size || 25), data.count)} de {data.count}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltros((f) => ({ ...f, page: (f.page || 1) - 1 }))}
                    disabled={(filtros.page || 1) === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">
                    Página {filtros.page || 1} de {data.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltros((f) => ({ ...f, page: (f.page || 1) + 1 }))}
                    disabled={(filtros.page || 1) === data.total_pages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <FileSpreadsheet className="mx-auto h-12 w-12 text-teal-500" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron solicitudes</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Ajusta los filtros o espera nuevas solicitudes
            </p>
          </div>
        )}
      </Card>

      {/* Seguimiento Modal */}
      <SeguimientoModal
        solicitudId={selectedSolicitud}
        open={!!selectedSolicitud}
        onOpenChange={(open) => { if (!open) setSelectedSolicitud(null) }}
        onSave={handleSaveSeguimiento}
        isSaving={actualizarSeguimiento.isPending}
      />
    </div>
  )
}
